import { Fragment, useEffect, useState, KeyboardEvent, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import { useFloating, offset, flip, shift, autoUpdate, Placement } from '@floating-ui/react';
import { Icon, IconProps, useWindowSize, MOBILE_MAX_SIZE, Button } from '@sendible/design-system';
import { Action, Backdrop, Children, Container, Content, Header, ListItem, Badge, BadgeContainer, ActionFeedback, Spacer } from './index.styles';

export interface DropdownOption {
  action: string;
  icon?: IconProps['name'];
  isDisabled?: boolean;
  isHighlighted?: boolean;
  label: string;
  badge?: string;
  altLabel?: string;
}

export interface FeedbackMessage {
  message: string;
  type?: 'danger' | 'info';
}

export interface ComposeDropdownProps extends Component {
  /**
   * List of options. Required.
   */
  options: DropdownOption[];
  /**
   * Component to be displayed at the header of the dropdown
   */
  renderHeader?: () => React.ReactNode;
  /**
   * ComposeDropDown opening position. Optional.
   */
  placement?: Placement;
  /**
   * Option select event emitter. Required.
   */
  select: (option: string, index: number) => void;
  /**
   * Visibiity event emitter. Optional.
   */
  visibilityChange?: (state: boolean) => void;
}

export const ComposeDropDown = ({ children, options, renderHeader, placement = 'right-start', select, visibilityChange }: ComposeDropdownProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const isMounted = useRef(true);
  const { width: w } = useWindowSize();
  const { ref: inViewRef } = useInView({
    threshold: 1,
    rootMargin: '-8px',
    onChange: (inView) => {
      if (!inView) {
        setIsVisible(false);
      }
    },
  });

  const {
    x,
    y,
    strategy,
    refs: { setReference: assignReference, setFloating: assignFloating },
  } = useFloating({
    open: isVisible,
    onOpenChange: setIsVisible,
    placement,
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const parentElementDoesNotHaveId = (element: HTMLUnknownElement, id: string) => {
    if (element.id === id) return true;

    let parent = element.parentElement;

    while (parent) {
      if (parent.id === id) return false;
      parent = parent.parentElement;
    }

    return true;
  };

  const handleKeyDown = (key: KeyboardEvent, index: number, listLength: number) => {
    key.preventDefault();

    if (key.code === 'ArrowUp' && index > 0) {
      const { previousSibling } = document.activeElement.parentElement;

      if (previousSibling.role === 'presentation') {
        previousSibling.previousSibling.firstChild.focus();
      } else {
        previousSibling.firstChild.focus();
      }
    }
    if (key.code === 'ArrowDown' && index < listLength - 1) {
      const { nextSibling } = document.activeElement.parentElement;

      if (nextSibling.role === 'presentation') {
        nextSibling.nextSibling.firstChild.focus();
      } else {
        nextSibling.firstChild.focus();
      }
    }
    if (key.code === 'Escape') {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      if (parentElementDoesNotHaveId(target, 'dd')) {
        setIsVisible(false);
      }
    };

    const handleWindowBlur = () => {
      setTimeout(() => {
        if (isMounted.current && document.activeElement?.tagName === 'IFRAME') {
          setIsVisible(false);
        }
      }, 0);
    };

    document.addEventListener('mouseup', handleClickOutside);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      isMounted.current = false;
      document.removeEventListener('mouseup', handleClickOutside);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [inViewRef]);

  useEffect(() => {
    if (visibilityChange) visibilityChange(isVisible);
  }, [isVisible]);

  return (
    <>
      <Children
        onClick={() => setIsVisible((prev) => !prev)}
        ref={assignReference}
        className="dropdown-link"
        data-testid="sidebar-dropdown-link"
      >
        {children}
      </Children>
      {isVisible &&
        createPortal(
          <>
            <AnimatePresence>
              <MotionConfig transition={{ duration: 0.33, ease: 'easeInOut' }}>
                <Container
                  id="dd"
                  ref={assignFloating}
                  style={{ position: strategy, top: y ?? 0, left: x ?? 0 }}
                  initial={w <= MOBILE_MAX_SIZE ? { y: '100vh', opacity: 0 } : {}}
                  animate={w <= MOBILE_MAX_SIZE ? { y: 0, opacity: 1 } : {}}
                  exit={w <= MOBILE_MAX_SIZE ? { y: '100vh', opacity: 0 } : {}}
                >
                  {w <= MOBILE_MAX_SIZE && (
                    <Header>
                      <Button
                        onClick={() => setIsVisible(false)}
                        icon="close_lg"
                        size={16}
                      />
                    </Header>
                  )}
                  {renderHeader && renderHeader()}
                  <Content ref={inViewRef}>
                    {options.map(({ action, icon, isDisabled, isHighlighted, label, badge, altLabel }, index) => (
                      <Fragment key={`dropdown-list-${index}`}>
                        {isHighlighted && options.length > 1 && (
                          <Spacer
                            role="presentation"
                            tabIndex={-1}
                            key={`dropdown-spacer-${index}`}
                          />
                        )}
                        <ListItem
                          key={`dropdown-item-${index}`}
                          className="dd-item"
                          onKeyDown={(event: KeyboardEvent<HTMLElement>) => {
                            handleKeyDown(event as KeyboardEvent, index, options.length);
                          }}
                        >
                          <Action
                            autoFocus={index === 0}
                            className="dd-action-button"
                            disabled={isDisabled}
                            data-testid={`sidebar-dropdown-item-link-${index}`}
                            onClick={() => {
                              select(action, index);
                              setIsVisible(false);
                            }}
                          >
                            {icon && <Icon name={icon} />}
                            {label}
                            {badge && (
                              <BadgeContainer>
                                <Badge>{badge}</Badge>
                              </BadgeContainer>
                            )}
                            {altLabel && <ActionFeedback>{altLabel}</ActionFeedback>}
                          </Action>
                        </ListItem>
                      </Fragment>
                    ))}
                  </Content>
                </Container>
              </MotionConfig>
            </AnimatePresence>
            <AnimatePresence>
              <MotionConfig transition={{ duration: 0.33, ease: 'easeInOut' }}>
                <Backdrop
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                />
              </MotionConfig>
            </AnimatePresence>
          </>,
          document.getElementById('dropdown-root') as HTMLElement
        )}
    </>
  );
};
